import React from 'react'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@material-ui/core'
import { Sort } from '@src/types/graphql-types'

export interface DataColumn<T = any> {
  headElement?: JSX.Element
  bodyElement?: (
    data: T,
    index?: number | undefined,
    noLink?: boolean | undefined
  ) => JSX.Element | string | null
  width?: string
  textAlign?: 'center' | 'inherit' | 'justify' | 'left' | 'right'
  padding?: 'checkbox' | 'default' | 'normal' | 'none'
  errorMessages?: (data: T) => string[]
}

interface TableProps<T = any> {
  data: T[]
  columns: DataColumn<T>[]
  initialSortColumn?: string
  initialSortDirection?: Sort
  verticalAlign?: 'top' | 'middle' | 'bottom' | 'baseline'
  noDataComponent?: JSX.Element
  afterRow?: (data) => JSX.Element
  rowSelected?: (currentValue: any) => boolean
  onRowClick?: (currentValue: any) => void
  errrorMessages?: (data) => JSX.Element
  tableCellClass?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    display: 'table',
    borderCollapse: 'collapse',
    borderSpacing: '0 2px',
    '& .MuiTableCell-root.MuiTableCell-paddingCheckbox .MuiFormControlLabel-root':
      {
        margin: 0,
        marginLeft: -9,
      },
  },
  tableHead: {
    display: 'table-header-group',
    '& .MuiTableCell-head': {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(12),
      padding: theme.spacing(0, 3),
      height: 47,
      background: theme.palette.background.grey,
    },
  },
  headElement: {
    display: 'inline-flex',
    border: 0,
    padding: 0,
    background: 'transparent',
    alignItems: 'center',
  },
  headLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 900,
    color: theme.palette.primary.main,
  },
  headUnsortable: {
    color: theme.palette.common.black,
  },
  tableBody: {
    '& .MuiTableRow-root': {
      width: '100%',
      backgroundColor: theme.palette.common.white,
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      '&.Mui-selected': {
        backgroundColor: '#f1eef6',
      },
      '& .MuiTableCell-root': {
        borderStyle: 'none',
        wordBreak: 'break-word',
        fontSize: theme.typography.pxToRem(14),
      },
    },
    '& tr:last-child': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  emptyRow: {
    borderBottom: 'none !important',
    '& td': {
      height: '35px !important',
      padding: '0px !important',
    },
  },
  tableHeight: {
    maxHeight: 573,
  },
  tabelCell: {
    padding: theme.spacing(4, 3),
    lineHeight: 1.1,
  },
}))

/**
 * Renders a table of given data with sortable columns
 *
 * Can be paired with ResponsiveDataAccordion for a better mobile experience
 */
const DataTable: React.FC<TableProps> = ({
  data,
  columns,
  noDataComponent,
  afterRow,
  verticalAlign = 'middle',
  tableCellClass,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  return (
    <TableContainer className={classes.tableHeight}>
      <Table className={classes.table} stickyHeader>
        <colgroup>
          {columns.map((column, index) => {
            return column.width ? (
              <col key={index} width={column.width} />
            ) : (
              <col key={index} />
            )
          })}
        </colgroup>
        <TableHead className={classes.tableHead}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={index}
                align={(isDesktop ? column.textAlign : 'left') || 'inherit'}
                padding={column.padding || 'normal'}
              >
                <div className={classes.headUnsortable}>
                  {column.headElement}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {data.length === 0 && (
            <TableRow className={classes.emptyRow}>
              <TableCell colSpan={columns.length}>{noDataComponent}</TableCell>
            </TableRow>
          )}
          {data.length > 0 &&
            data.map((currentValue, dataIndex) => {
              if (currentValue?.isCurrentForm) return null

              return (
                <React.Fragment key={dataIndex}>
                  <TableRow>
                    {columns.map((column, columnIndex) => {
                      const columnBody =
                        column.bodyElement &&
                        column.bodyElement(currentValue, dataIndex)
                      return columnBody ? (
                        <TableCell
                          key={columnIndex}
                          align={column.textAlign || 'inherit'}
                          style={{ verticalAlign }}
                          className={tableCellClass || classes.tabelCell}
                        >
                          {columnBody}
                        </TableCell>
                      ) : null
                    })}
                  </TableRow>
                  {afterRow && afterRow(currentValue)}
                </React.Fragment>
              )
            })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default DataTable
